import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, ScanLine, Send } from "lucide-react";
import { UploadDropzone } from "../components/pictolead/UploadDropzone";
import { ContactReviewCard } from "../components/pictolead/ContactReviewCard";
import { PicSettingsBar } from "../components/pictolead/PicSettingsBar";
import { ImportHistory } from "../components/pictolead/ImportHistory";
import { CameraCapture } from "../components/pictolead/CameraCapture";
import { FreeScanMeter } from "../components/pictolead/FreeScanMeter";
import { refreshPlanAccess, usePlanAccess } from "../hooks/usePlanAccess";
import { openUpgrade } from "../components/UpgradeDialog";

const MAX_FILES = 10;

export default function PicToLead() {
  const [settings, setSettings] = useState({ auto_import: false, default_tags: [], loading: true });
  const [queue, setQueue] = useState([]);
  const [results, setResults] = useState([]);
  const [reading, setReading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const plan = usePlanAccess();

  useEffect(() => {
    axios.get(`${API}/pic-to-lead/settings`).then((r) => setSettings(r.data)).catch(() => setSettings((s) => ({ ...s, loading: false })));
  }, []);

  useEffect(() => () => queue.forEach((q) => URL.revokeObjectURL(q.preview)), [queue]);

  const saveSettings = async (next) => {
    setSettings(next);
    try {
      await axios.put(`${API}/pic-to-lead/settings`, next);
      toast.success("Pic-to-Lead settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    }
  };

  const addFiles = useCallback((files) => {
    setQueue((prev) => {
      const room = MAX_FILES - prev.length;
      if (files.length > room) toast.warning(`Up to ${MAX_FILES} photos at a time`);
      const added = files.slice(0, Math.max(room, 0)).map((file) => ({
        id: `${file.name}-${file.size}-${Math.random()}`, file, preview: URL.createObjectURL(file),
      }));
      return [...prev, ...added];
    });
  }, []);

  const scanItems = async (items) => {
    const form = new FormData();
    items.forEach((q) => form.append("files", q.file));
    const { data } = await axios.post(`${API}/pic-to-lead/scan`, form, { timeout: 180000 });
    const mapped = data.results.map((r, i) => ({
      ...r, key: items[i].id, preview: URL.createObjectURL(items[i].file),
      selected: r.success && !r.import, status: r.import?.status, error: r.import?.error || r.error,
    }));
    if (mapped.some((m) => m.status === "success")) setHistoryKey((k) => k + 1);
    refreshPlanAccess().then((d) => {
      const u = d?.pic_to_lead_usage;
      if (u && u.limit != null && u.remaining === 0) openUpgrade("pic_last");
    });
    return { mapped, autoImport: data.auto_import };
  };

  const outOfScans = () => {
    const u = plan?.pic_to_lead_usage;
    if (u && u.limit != null && u.remaining <= 0) {
      openUpgrade("pic_out");
      return true;
    }
    return false;
  };

  const readPhotos = async () => {
    if (!queue.length || outOfScans()) return;
    setReading(true);
    try {
      const { mapped, autoImport } = await scanItems(queue);
      setResults((prev) => [...mapped, ...prev]);
      setQueue([]);
      const ok = mapped.filter((m) => m.success).length;
      const imported = mapped.filter((m) => m.status === "success").length;
      if (autoImport) toast.success(`Read ${ok} of ${mapped.length} photos · ${imported} sent to your CRM`);
      else toast.success(`Read ${ok} of ${mapped.length} photos — review and import below`);
    } catch (e) {
      if (e.response?.status === 403) openUpgrade("pic_out");
      else toast.error(e.response?.data?.detail || "Could not read the photos");
    } finally {
      setReading(false);
    }
  };

  const snapPhoto = async (file) => {
    if (outOfScans()) return;
    const item = { id: `snap-${Date.now()}-${Math.random()}`, file };
    setResults((prev) => [{ key: item.id, file_name: file.name || "Camera photo", preview: URL.createObjectURL(file), reading: true }, ...prev]);
    try {
      const { mapped } = await scanItems([item]);
      setResults((prev) => prev.map((r) => (r.key === item.id ? { ...mapped[0], file_name: r.file_name } : r)));
    } catch (e) {
      if (e.response?.status === 403) openUpgrade("pic_out");
      setResults((prev) => prev.map((r) => (r.key === item.id
        ? { ...r, reading: false, success: false, error: e.response?.data?.detail || "Could not read this photo" } : r)));
    }
  };

  const updateResult = (key, patch) => setResults((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeResult = (key) => setResults((prev) => prev.filter((r) => r.key !== key));

  const importItems = async (items) => {
    if (!items.length) return;
    setImporting(true);
    try {
      const contacts = items.map((r) => ({ ...r.contact, file_name: r.file_name }));
      const { data } = await axios.post(`${API}/pic-to-lead/import`, { contacts, tags: settings.default_tags });
      data.results.forEach((rec, i) => updateResult(items[i].key, {
        status: rec.status, error: rec.error, selected: rec.status !== "success",
      }));
      const failed = data.results.length - data.imported;
      if (data.imported) toast.success(`${data.imported} contact${data.imported > 1 ? "s" : ""} sent to your CRM`);
      if (failed) toast.error(`${failed} contact${failed > 1 ? "s" : ""} could not be imported`);
      setHistoryKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const selected = results.filter((r) => r.selected && r.success && r.status !== "success");

  return (
    <div data-testid="pic-to-lead-page">
      <div className="page-header">
        <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Camera size={28} /> Pic-to-Lead
        </h1>
        <p className="page-description">
          Snap or upload business cards, badges or flyers — we read the contact details and send them straight to your CRM.
        </p>
      </div>

      <FreeScanMeter />
      <PicSettingsBar settings={settings} onSave={saveSettings} />

      <div className="content-card" data-testid="pic-upload-card">
        <CameraCapture pending={results.filter((r) => r.reading).length} autoImport={settings.auto_import} onSnap={snapPhoto} />
        <UploadDropzone queue={queue} max={MAX_FILES} disabled={reading} onAdd={addFiles}
          onRemove={(id) => setQueue((prev) => prev.filter((q) => q.id !== id))} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px", flexWrap: "wrap" }}>
          {queue.length > 0 && !reading && (
            <Button variant="outline" onClick={() => setQueue([])} data-testid="pic-clear-queue-btn">Clear</Button>
          )}
          <Button onClick={readPhotos} disabled={!queue.length || reading} data-testid="pic-read-btn">
            {reading
              ? <><Loader2 size={16} className="animate-spin" style={{ marginRight: 8 }} /> Reading {queue.length} photo{queue.length > 1 ? "s" : ""}…</>
              : <><ScanLine size={16} style={{ marginRight: 8 }} /> {settings.auto_import ? "Read & import" : "Read"} {queue.length || ""} photo{queue.length === 1 ? "" : "s"}</>}
          </Button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="content-card" data-testid="pic-results-card">
          <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
            <h2 className="card-title">Review contacts ({results.length})</h2>
            <Button onClick={() => importItems(selected)} disabled={!selected.length || importing} data-testid="pic-import-selected-btn">
              {importing ? <Loader2 size={16} className="animate-spin" style={{ marginRight: 8 }} /> : <Send size={16} style={{ marginRight: 8 }} />}
              Import selected ({selected.length})
            </Button>
          </div>
          <div style={{ display: "grid", gap: "16px" }}>
            {results.map((r) => (
              <ContactReviewCard key={r.key} item={r} busy={importing}
                onChange={(patch) => updateResult(r.key, patch)}
                onImport={() => importItems([r])} onRemove={() => removeResult(r.key)} />
            ))}
          </div>
        </div>
      )}

      <ImportHistory refreshKey={historyKey} />
    </div>
  );
}
